import React, { useEffect, useState, useRef } from "react";
import { X, Plus, Save, Trash2, Upload } from "lucide-react";
import api, { apiError, resolveUrl } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Select } from "@/pages/Projects";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const ROLES = ["admin", "pmo", "pm", "sponsor", "member"];

export const AdminLists = () => {
  const [lists, setLists] = useState([]);
  const [draft, setDraft] = useState({});

  const load = () => api.get("/config/lists").then(({ data }) => setLists(data));
  useEffect(() => { load(); }, []);

  const addValue = async (l) => {
    const v = (draft[l.key] || "").trim();
    if (!v) return;
    try {
      await api.put(`/config/lists/${l.key}`, { values: [...l.values, v], label: l.label });
      setDraft({ ...draft, [l.key]: "" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeValue = async (l, v) => {
    try {
      await api.put(`/config/lists/${l.key}`, { values: l.values.filter((x) => x !== v), label: l.label });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <Shell testId="admin-lists-page" title="Configurable Lists" subtitle="Dropdown values used across project records and RAID logs">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {lists.map((l) => (
          <div key={l.key} data-testid={`list-card-${l.key}`} className="panel p-5">
            <h3 className="font-display text-base font-semibold text-slate-800">{l.label}</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {l.values.map((v) => (
                <span key={v} data-testid={`list-value-${l.key}-${v}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">
                  {v}
                  <button data-testid={`remove-${l.key}-${v}`} onClick={() => removeValue(l, v)} aria-label={`Remove ${v}`} title={`Remove ${v}`} className="text-slate-400 transition-colors hover:text-red-600"><X size={12} /></button>
                </span>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Input data-testid={`add-input-${l.key}`} value={draft[l.key] || ""} onChange={(e) => setDraft({ ...draft, [l.key]: e.target.value })} placeholder={`Add ${l.label.toLowerCase()} value`} onKeyDown={(e) => e.key === "Enter" && addValue(l)} />
              <Button data-testid={`add-btn-${l.key}`} onClick={() => addValue(l)} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}><Plus size={14} /> Add</Button>
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
};

export const AdminUsers = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [scoping, setScoping] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [projects, setProjects] = useState([]);

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    Promise.all([api.get("/portfolios"), api.get("/programmes"), api.get("/projects")])
      .then(([pf, pg, pr]) => { setPortfolios(pf.data); setProgrammes(pg.data); setProjects(pr.data); })
      .catch(() => {});
  }, []);

  const toggle = (key, id) =>
    setScoping((s) => {
      const list = s[key] || [];
      return { ...s, [key]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] };
    });

  const saveScope = async () => {
    try {
      await api.put(`/users/${scoping.id}/scope`, {
        portfolio_ids: scoping.portfolio_ids || [],
        programme_ids: scoping.programme_ids || [],
        project_ids: scoping.project_ids || [],
      });
      setScoping(null);
      await load();
      toast.success("Access updated");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const create = async () => {
    if (!form.name || !form.email || form.password.length < 6)
      return toast.error("Name, email and a 6+ character password are required");
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ name: "", email: "", password: "", role: "member" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const setRole = async (id, role) => {
    try {
      await api.put(`/users/${id}/role`, { role });
      load();
      toast.success("Role updated");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      load();
      toast.success("User removed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <Shell testId="admin-users-page" title="Users & Roles" subtitle="Role-based access across the workspace">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="panel p-5" data-testid="create-user-panel">
          <h3 className="font-display text-base font-semibold text-slate-800">Add User</h3>
          <div className="mt-4 space-y-3">
            <div><label className="field-label">Name</label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="field-label">Email</label><Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><label className="field-label">Password</label><Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div><label className="field-label">Role</label><Select testId="user-role-select" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={ROLES} placeholder="Select role" /></div>
            <Button data-testid="create-user-btn" onClick={create} className="w-full gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}><Plus size={14} /> Create User</Button>
          </div>
        </div>

        <div className="panel overflow-hidden lg:col-span-2" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>{["Name", "Email", "Role", "Access", ""].map((h) => <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{u.name}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <div className="w-32">
                      <Select testId={`role-select-${u.id}`} value={u.role} onChange={(v) => setRole(u.id, v)} options={ROLES} placeholder="Role" />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {["admin", "pmo"].includes(u.role) ? (
                      <span className="text-xs text-slate-400">All projects</span>
                    ) : (
                      <button
                        data-testid={`scope-user-${u.id}`}
                        onClick={() => setScoping({ id: u.id, name: u.name, portfolio_ids: u.portfolio_ids || [], programme_ids: u.programme_ids || [], project_ids: u.project_ids || [] })}
                        className="text-xs font-semibold underline hover:text-slate-900"
                        style={{ color: "var(--brand-primary)" }}
                      >
                        {(u.portfolio_ids?.length || 0) + (u.programme_ids?.length || 0) + (u.project_ids?.length || 0)} assigned
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {u.id !== user?.id && (
                      <button data-testid={`delete-user-${u.id}`} onClick={() => window.confirm(`Remove ${u.name}?`) && remove(u.id)} aria-label="Delete user" title="Delete user" className="text-slate-400 transition-colors hover:text-red-600"><Trash2 size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!scoping} onOpenChange={(o) => !o && setScoping(null)}>
        <DialogContent data-testid="user-scope-dialog" className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Project Access</DialogTitle>
            <DialogDescription>
              {scoping?.name} sees only what is assigned here. Assigning a portfolio or programme includes everything underneath it.
            </DialogDescription>
          </DialogHeader>
          {scoping && (
            <div className="space-y-4">
              {[
                { key: "portfolio_ids", label: "Portfolios", items: portfolios },
                { key: "programme_ids", label: "Programmes", items: programmes },
                { key: "project_ids", label: "Individual Projects", items: projects },
              ].map((g) => (
                <div key={g.key}>
                  <h4 className="field-label">{g.label}</h4>
                  {g.items.length === 0 ? (
                    <p className="text-xs text-slate-400">None available.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {g.items.map((it) => {
                        const on = (scoping[g.key] || []).includes(it.id);
                        return (
                          <button
                            key={it.id}
                            data-testid={`scope-${g.key}-${it.id}`}
                            onClick={() => toggle(g.key, it.id)}
                            className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-transparent text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                            style={on ? { backgroundColor: "var(--brand-primary)" } : undefined}
                          >
                            {it.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="scope-cancel-btn" onClick={() => setScoping(null)}>Cancel</Button>
            <Button data-testid="scope-save-btn" onClick={saveScope} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
              <Save size={14} /> Save Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
};

export const AdminBranding = () => {
  const { branding, refresh } = useBranding();
  const [form, setForm] = useState(branding);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => setForm(branding), [branding]);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    setUploading(true);
    try {
      const { data: res } = await api.post("/branding/logo", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo_url: res.logo_url }));
      await refresh();
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    try {
      await api.put("/branding", form);
      await refresh();
      toast.success("Branding applied — theme updated instantly");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const fields = [
    ["product_name", "Product Name", "text"],
    ["org_name", "Client / Workspace Name", "text"],
    ["favicon_url", "Favicon URL", "text"],
    ["primary_color", "Primary Colour", "color"],
    ["secondary_color", "Secondary Colour", "color"],
    ["accent_color", "Accent Colour", "color"],
  ];

  return (
    <Shell testId="admin-branding-page" title="White-Label Settings" subtitle="Runtime theming — no rebuild required per client">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="panel p-5" data-testid="branding-form">
          {fields.map(([k, label, type]) => (
            <div key={k} className="mb-4">
              <label className="field-label">{label}</label>
              <div className="flex items-center gap-3">
                {type === "color" && (
                  <input data-testid={`branding-${k}-picker`} type="color" value={form[k] || "#000000"} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-9 w-12 cursor-pointer rounded border border-slate-200" />
                )}
                <Input data-testid={`branding-${k}-input`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </div>
            </div>
          ))}
          <div className="mb-5">
            <label className="field-label">Client Logo</label>
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-28 items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50">
                {form.logo_url ? (
                  <img data-testid="branding-logo-thumb" src={resolveUrl(form.logo_url)} alt="logo" className="max-h-12 max-w-24 object-contain" />
                ) : (
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">No logo</span>
                )}
              </div>
              <div>
                <input ref={fileRef} data-testid="branding-logo-file" type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" onChange={uploadLogo} className="hidden" />
                <Button variant="outline" data-testid="branding-logo-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="gap-1.5">
                  <Upload size={14} /> {uploading ? "Uploading…" : form.logo_url ? "Replace Logo" : "Upload Logo"}
                </Button>
                <p className="mt-1.5 text-[11px] text-slate-400">PNG, JPG, WEBP or SVG · max 2MB</p>
              </div>
            </div>
          </div>
          <Button data-testid="save-branding-btn" onClick={save} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}><Save size={14} /> Apply Branding</Button>
        </div>

        <div className="panel overflow-hidden" data-testid="branding-preview">
          <div className="px-5 pt-5"><h3 className="font-display text-base font-semibold text-slate-800">Live Preview</h3></div>
          <div className="mt-4 p-5">
            <div className="overflow-hidden rounded-md border border-slate-200">
              <div className="flex items-center gap-3 px-4 py-3" style={{ background: `linear-gradient(100deg, ${form.primary_color} 0%, ${form.secondary_color} 100%)` }}>
                {form.logo_url ? (
                  <img src={resolveUrl(form.logo_url)} alt="preview logo" className="h-7 w-auto max-w-[120px] object-contain" />
                ) : (
                  <div className="flex h-7 w-7 items-center justify-center rounded bg-white/15 text-xs font-bold text-white">{(form.product_name || "PH").slice(0, 2).toUpperCase()}</div>
                )}
                <div className="font-display text-sm font-bold text-white">{form.product_name}</div>
              </div>
              <div className="flex gap-2 bg-[#f5f7fa] px-4 py-3">
                {["Home", "Delivery", "Structure", "Admin"].map((t) => (
                  <span key={t} className="rounded px-2 py-1 text-xs font-medium text-slate-600">{t}</span>
                ))}
              </div>
              <div className="space-y-3 bg-white p-4">
                <button className="rounded px-4 py-2 text-xs font-semibold text-white" style={{ backgroundColor: form.primary_color }}>Primary Action</button>
                <button className="ml-2 rounded px-4 py-2 text-xs font-semibold text-white" style={{ backgroundColor: form.accent_color }}>Accent Action</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
};
